"use client";
/**
 * What the notch drew, drawn in the Dock window's own vocabulary (contract
 * 2026-09-23; founder law "one face", no new card): the tasks as the crown's
 * status dots beside the ball, the shelf as the island the Dock already has,
 * a moment on the crown's paper, and the Mac's state as the row's note. Each
 * clickable piece carries data-dock-hit, so the shell grants its clicks.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  actOnDockMoment,
  forwardRoute,
  setDockLineLive,
  type DockMoment,
  type DockTask,
} from "@/lib/dock-window";
import type { FleetRow } from "@/components/composer/fleet";
import {
  bloomOf,
  dockStatusLine,
  dockTaskRows,
  putAnswerAway,
  useDockShell,
  useDockShellStore,
  useInDockWindow,
  type DockReply,
} from "./dockShell";
import { useLiveStore } from "./liveStore";
import { askFromMoment } from "./momentOffer";

/** The crown's dot vocabulary: green pulses while working, amber while it needs you. */
const DOT_STATUS: Record<DockTask["state"], string | undefined> = {
  working: undefined,
  needs: "needs",
  done: undefined,
  // Amber like its NEEDS YOU chip on the shelf (dockTaskRows), still: it asks for a look.
  failed: "needs",
};
const COUNT_WORDS: Record<DockTask["state"], string> = {
  working: "working",
  needs: "need you",
  done: "done",
  failed: "stopped",
};

/** One dot per task the shell tracks, beside the ball; a click opens the shelf. */
export function DockTaskDots({ tasks }: { tasks: DockTask[] }) {
  if (tasks.length === 0) return null;
  const counts = new Map<DockTask["state"], number>();
  for (const t of tasks) counts.set(t.state, (counts.get(t.state) ?? 0) + 1);
  const said = [...counts]
    .map(([state, n]) => `${n} ${COUNT_WORDS[state]}`)
    .join(", ");
  return (
    <button
      type="button"
      data-dock-hit
      data-testid="dock-task-dots"
      aria-label={`Your tasks: ${said}`}
      onClick={() => useLiveStore.getState().setTrayOpen(true)}
      className="absolute top-1/2 left-[calc(100%+6px)] flex -translate-y-1/2 cursor-pointer flex-col items-center gap-1 rounded-full border-0 bg-transparent p-1.5 hover:bg-bg-muted"
    >
      {tasks.map((t) => (
        <span
          key={t.id}
          className={cn(
            "cl-crown__dot",
            (t.state === "done" || t.state === "failed") &&
              "cl-crown__dot--still"
          )}
          data-status={DOT_STATUS[t.state]}
          data-state={t.state}
        />
      ))}
    </button>
  );
}

const ACT =
  "rounded-8 border-none px-2.5 py-1 text-3 font-semibold cursor-pointer [font-family:inherit] transition-[background] duration-150 ease-out disabled:cursor-default disabled:opacity-55";

/** A moment on the crown's paper: the notch's offer or update prompt, answered here. */
export function DockMomentCard({ moment }: { moment: DockMoment }) {
  const [busy, setBusy] = useState(false);
  const answer = async (accept: boolean) => {
    setBusy(true);
    const prompt = await actOnDockMoment(moment.id, accept);
    // The shell clears it too (its next emit); the card goes now either way.
    useDockShellStore.setState({ moment: null });
    setBusy(false);
    if (prompt)
      askFromMoment(prompt, moment.title).catch((err: unknown) =>
        console.error("[dock] moment offer failed", err)
      );
  };
  return (
    <div
      className="cl-crown"
      data-up="1"
      data-dock-hit
      data-testid="dock-moment"
    >
      <div className="cl-crown__paper">
        <span className="cl-crown__say font-medium">{moment.title}</span>
        {moment.body && <span className="cl-crown__swap">{moment.body}</span>}
        <div className="mt-2.5 flex justify-end gap-1.5">
          <button
            type="button"
            disabled={busy}
            className={cn(
              ACT,
              "bg-[color-mix(in_srgb,var(--fg-default)_8%,transparent)] text-fg-default hover:bg-[color-mix(in_srgb,var(--fg-default)_13%,transparent)]"
            )}
            onClick={() => void answer(false)}
          >
            {moment.dismissLabel}
          </button>
          <button
            type="button"
            disabled={busy}
            className={cn(ACT, "bg-fg-default text-bg-base hover:opacity-90")}
            onClick={() => void answer(true)}
          >
            {moment.acceptLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Words on the crown's paper that stay up until put away, as the notch's
 * reply did: a done task's answer (its bloom) or the reply to an accepted
 * offer. With a task behind it the paper is the door to it, in the main window.
 */
function CrownWords({
  testId,
  title,
  text,
  taskId,
  onDismiss,
}: {
  testId: string;
  title: string;
  text: string;
  taskId?: string;
  onDismiss: () => void;
}) {
  const body = (
    <span className="cl-crown__t">
      <span className="cl-crown__say font-medium">{title}</span>
      <span className="cl-crown__swap line-clamp-4">{text}</span>
    </span>
  );
  return (
    <div className="cl-crown" data-up="1" data-dock-hit data-testid={testId}>
      <div className="cl-crown__paper relative">
        {taskId ? (
          <button
            type="button"
            className="cl-crown__row pr-6"
            aria-label={`Open task: ${title}`}
            onClick={() => {
              onDismiss();
              forwardRoute(`/task/${taskId}`);
            }}
          >
            {body}
            <span className="cl-crown__chev" aria-hidden>
              ›
            </span>
          </button>
        ) : (
          <div className="cl-crown__row pr-6 cursor-default">{body}</div>
        )}
        <button
          type="button"
          aria-label="Dismiss"
          className="absolute top-2 right-2 grid h-6 w-6 cursor-pointer place-items-center rounded-full border-0 bg-transparent text-3.5 text-fg-faint hover:bg-bg-muted hover:text-fg-default"
          onClick={onDismiss}
        >
          ×
        </button>
      </div>
    </div>
  );
}

/** A done task's answer on the crown's paper (the notch's bloom). */
export function DockAnswerCard({ task }: { task: DockTask }) {
  return (
    <CrownWords
      testId="dock-answer"
      title={task.title}
      text={task.answer ?? ""}
      taskId={task.id}
      onDismiss={() => putAnswerAway(task.id)}
    />
  );
}

/** The reply to an accepted offer, on the crown's paper until put away. */
export function DockReplyCard({ reply }: { reply: DockReply }) {
  return (
    <CrownWords
      testId="dock-reply"
      title={reply.title}
      text={reply.text}
      taskId={reply.taskId}
      onDismiss={() => useDockShellStore.setState({ reply: null })}
    />
  );
}

export interface DockShellParts {
  /** The shelf's rows (the island's). */
  rows: FleetRow[];
  /** The moment, else an offer's reply, else a done task's answer, for the crown while the Line's own work is not on it. */
  crown: ReactNode;
  /** The task dots, beside the ball. */
  aside: ReactNode;
  /** The Mac's one line for the row, or null. */
  note: string | null;
}

/**
 * The window host's parts, inside the shell's panel only; null on every other
 * host and in a plain browser tab at /dock (nothing subscribed, nothing drawn,
 * the fleet's own rows kept).
 */
export function useDockShellParts(windowHost: boolean): DockShellParts | null {
  const on = useInDockWindow() && windowHost;
  useDockShell(on);
  // The Line's live level, told to the shell on mount and on every change
  // (contract addition): the panel shows in screen recordings only mid-call.
  const lineLive = useLiveStore(
    (s) => s.line.phase === "connecting" || s.line.phase === "open"
  );
  useEffect(() => {
    if (on) setDockLineLive(lineLive);
  }, [on, lineLive]);
  const tasks = useDockShellStore((s) => s.tasks);
  const moment = useDockShellStore((s) => s.moment);
  const status = useDockShellStore((s) => s.status);
  const seen = useDockShellStore((s) => s.seenAnswers);
  const reply = useDockShellStore((s) => s.reply);
  const rows = useMemo(() => dockTaskRows(tasks), [tasks]);
  if (!on) return null;
  const bloom = bloomOf(tasks, seen);
  return {
    rows,
    crown: moment ? (
      <DockMomentCard moment={moment} />
    ) : reply ? (
      <DockReplyCard reply={reply} />
    ) : bloom ? (
      <DockAnswerCard task={bloom} />
    ) : null,
    aside: <DockTaskDots tasks={tasks} />,
    note: dockStatusLine(status),
  };
}
